import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "@/analytics";
import { addDays, isWeekend, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft, ArrowRight, Calendar, CheckCircle2, Clock, Loader2, MessageCircle, Video,
} from "lucide-react";
import { SectionLabel, MaskedLine } from "@/components/Reveal";
import { WPP_URL } from "@/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  {
    id: "venda",
    title: "Reunião Estratégica",
    minutes: 30,
    desc: "Uma conversa objetiva sobre o momento da sua marca e como a Fuchs pode ajudar.",
  },
  {
    id: "alinhamento",
    title: "Sessão executiva de alinhamento estratégico",
    minutes: 60,
    desc: "Sessão profunda para alinhar direção, prioridades e próximos passos do projeto.",
  },
];

const QUALIF = [
  { key: "revenue", label: "Faturamento médio mensal", options: ["Até R$ 50 mil", "R$ 50 mil a R$ 200 mil", "R$ 200 mil a R$ 1 milhão", "Acima de R$ 1 milhão"] },
  { key: "employees", label: "Número de funcionários", options: ["1 a 5", "6 a 20", "21 a 50", "Mais de 50"] },
  { key: "urgency", label: "Urgência para resolver a maior dor da empresa", options: ["Preciso resolver agora", "Nos próximos 3 meses", "Estou pesquisando"] },
  { key: "investment", label: "Disponibilidade para investir nas soluções propostas", options: ["Tenho orçamento aprovado", "Consigo aprovar com a diretoria", "Ainda não tenho previsão"] },
];

const nextWeekdays = (n) => {
  const days = [];
  let d = new Date();
  while (days.length < n) {
    if (!isWeekend(d)) days.push(d);
    d = addDays(d, 1);
  }
  return days;
};

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-5 py-4 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-orange";

const StepDots = ({ step }) => (
  <div className="flex items-center gap-2" data-testid="booking-steps">
    {["Tipo", "Data", "Horário", "Seus dados"].map((label, i) => (
      <div key={label} className="flex items-center gap-2">
        <span
          className={`flex h-7 w-7 items-center justify-center border font-mono text-[10px] ${
            step > i ? "border-brand-orange bg-brand-orange text-white" : "border-white/15 text-slate-500"
          }`}
        >
          {i + 1}
        </span>
        <span className={`hidden font-mono text-[10px] uppercase tracking-[0.2em] sm:block ${step > i ? "text-white" : "text-slate-600"}`}>
          {label}
        </span>
        {i < 3 && <span className="h-px w-6 bg-white/15" />}
      </div>
    ))}
  </div>
);

export default function Agendar() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", company: "", segment: "", revenue: "", employees: "", urgency: "", investment: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const days = nextWeekdays(14);
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const tipoParam = params.get("tipo") || "";
  const [clientCtx, setClientCtx] = useState(null);

  useEffect(() => {
    const t = TYPES.find((tp) => tp.id === tipoParam);
    if (t) setType(t);
  }, [tipoParam]);

  useEffect(() => {
    if (!ref) return;
    axios
      .get(`${API}/bookings/context`, { params: { ref } })
      .then(({ data }) => {
        setClientCtx(data);
        setForm((f) => ({
          ...f,
          name: data.name || f.name,
          email: data.email || f.email,
          whatsapp: data.whatsapp || f.whatsapp,
          company: data.company || f.company,
        }));
      })
      .catch(() => {});
  }, [ref]);

  useEffect(() => {
    if (!date || !type) return;
    setSlots(null);
    setTime(null);
    axios
      .get(`${API}/bookings/slots`, { params: { date: format(date, "yyyy-MM-dd"), type: type.id } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => toast.error("Não foi possível carregar os horários."));
  }, [date, type]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        type: type.id,
        date: format(date, "yyyy-MM-dd"),
        time,
        ...form,
        ref,
      });
      trackEvent("appointment_submitted", { appointment_type: type.id });
      setDone(data.booking);
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Não foi possível concluir o agendamento.");
      if (err.response?.status === 409) setStep(3);
    } finally {
      setSending(false);
    }
  };

  const wppConfirm = done
    ? `${WPP_URL}?text=${encodeURIComponent(
        `Olá! Acabei de agendar pelo site: ${TYPES.find((t) => t.id === done.type)?.title} em ${format(new Date(done.date + "T12:00:00"), "dd/MM/yyyy")} às ${done.time}. — ${done.name}`
      )}`
    : "";

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="hero-glow noise relative min-h-screen bg-ink px-6 pb-24 pt-32 lg:px-10 lg:pt-40"
    >
      <div className="mx-auto max-w-3xl">
        <div className="flex justify-center">
          <SectionLabel>Agendamento</SectionLabel>
        </div>
        <h1 className="mt-8 text-center font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
          <MaskedLine delay={0.15}>Escolha o melhor</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="font-serif font-medium italic text-brand-orange">horário</span> para conversarmos.
          </MaskedLine>
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-center text-sm leading-relaxed text-slate-400 sm:text-base">
          Segunda a sexta, das 8h às 11h e das 14h às 17h (horário de Brasília).
          A confirmação chega por e-mail — e você avisa a equipe no WhatsApp com um toque.
        </p>
        {clientCtx && (
          <div data-testid="booking-client-context" className="mx-auto mt-6 flex w-fit items-center gap-4 border border-brand-orange/40 bg-navy/50 px-5 py-3">
            {clientCtx.logo && (
              <img src={clientCtx.logo} alt={clientCtx.company || "Logomarca"} className="h-8 w-auto max-w-[100px] object-contain" />
            )}
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-orange">
              Link exclusivo · {clientCtx.name}
              {clientCtx.company ? ` · ${clientCtx.company}` : ""}
            </p>
          </div>
        )}

        {done ? (
          <motion.div
            data-testid="booking-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-14 border border-emerald-400/30 bg-navy/40 p-8 text-center backdrop-blur-sm lg:p-12"
          >
            <span className="mx-auto flex h-16 w-16 items-center justify-center border border-emerald-400/50 text-emerald-400">
              <CheckCircle2 size={28} />
            </span>
            <h2 className="mt-6 font-display text-2xl font-bold text-white sm:text-3xl">
              {done.status === "aguardando" ? "Pedido recebido." : "Agendado com sucesso."}
            </h2>
            <p className="mt-4 font-serif text-lg italic text-slate-300">
              {TYPES.find((t) => t.id === done.type)?.title} ·{" "}
              {format(new Date(done.date + "T12:00:00"), "EEEE, dd 'de' MMMM", { locale: ptBR })} às {done.time}
            </p>
            {done.status === "aguardando" ? (
              <p data-testid="booking-pending-notice" className="mx-auto mt-5 max-w-md border border-brand-orange/40 bg-ink/60 p-5 text-sm leading-relaxed text-slate-300">
                <span className="font-semibold text-brand-orange">A confirmação da reunião é feita pela equipe Fuchs.</span>{" "}
                Analisamos cada pedido pessoalmente e você recebe a confirmação em{" "}
                <span className="text-white">{done.email}</span>.
              </p>
            ) : (
              <p className="mt-3 text-sm text-slate-400">
                Enviamos a confirmação para <span className="text-white">{done.email}</span>.
              </p>
            )}
            <a
              href={wppConfirm}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-confirm"
              className="group mt-8 inline-flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-red"
            >
              <MessageCircle size={16} />
              Avisar a equipe no WhatsApp
            </a>
            <div className="mt-8">
              <button
                onClick={() => {
                  setDone(null);
                  setStep(1);
                  setType(null);
                  setDate(null);
                  setTime(null);
                  setForm({
                    name: clientCtx?.name || "",
                    email: clientCtx?.email || "",
                    whatsapp: clientCtx?.whatsapp || "",
                    company: clientCtx?.company || "",
                    segment: "", revenue: "", employees: "", urgency: "", investment: "",
                  });
                }}
                data-testid="booking-new-button"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-white"
              >
                Fazer outro agendamento
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="mt-12">
            <div className="flex justify-center">
              <StepDots step={step} />
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }} transition={{ duration: 0.4 }} className="mt-10 grid gap-5 sm:grid-cols-2">
                  {TYPES.filter((t) => t.id !== "alinhamento" || tipoParam === "alinhamento").map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setType(t);
                        setStep(2);
                      }}
                      data-testid={`booking-type-${t.id}`}
                      className={`group border p-7 text-left transition-all duration-300 hover:-translate-y-1 ${
                        type?.id === t.id ? "border-brand-orange bg-navy/60" : "border-white/10 bg-navy/30 hover:border-brand-orange/60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Video size={20} className="text-brand-orange" />
                        <span className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                          <Clock size={11} /> {t.minutes} min
                        </span>
                      </div>
                      <h3 className="mt-5 font-display text-xl font-bold leading-snug text-white group-hover:text-brand-orange">
                        {t.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.desc}</p>
                    </button>
                  ))}
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }} transition={{ duration: 0.4 }} className="mt-10">
                  <p className="mb-5 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    {type.title} · {type.minutes} minutos
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {days.map((d) => {
                      const selected = date && format(date, "yyyy-MM-dd") === format(d, "yyyy-MM-dd");
                      return (
                        <button
                          key={d.toISOString()}
                          onClick={() => {
                            setDate(d);
                            setStep(3);
                          }}
                          data-testid={`booking-date-${format(d, "yyyy-MM-dd")}`}
                          className={`border px-4 py-4 text-center transition-all duration-300 hover:-translate-y-0.5 ${
                            selected ? "border-brand-orange bg-brand-orange text-white" : "border-white/10 bg-navy/30 text-white hover:border-brand-orange/60"
                          }`}
                        >
                          <span className={`block font-mono text-[10px] uppercase tracking-[0.2em] ${selected ? "text-white/80" : "text-brand-orange"}`}>
                            {format(d, "EEE", { locale: ptBR })}
                          </span>
                          <span className="mt-1 block font-display text-lg font-bold">
                            {format(d, "dd/MM")}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <BackRow onBack={() => setStep(1)} />
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }} transition={{ duration: 0.4 }} className="mt-10">
                  <p className="mb-5 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    {format(date, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                  </p>
                  {slots === null ? (
                    <div className="flex items-center justify-center gap-3 py-14 text-slate-500">
                      <Loader2 size={18} className="animate-spin" /> Carregando horários...
                    </div>
                  ) : slots.length === 0 ? (
                    <p data-testid="booking-no-slots" className="border border-dashed border-white/15 py-14 text-center text-sm text-slate-500">
                      Sem horários livres neste dia. Escolha outra data.
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {slots.map((t) => (
                        <button
                          key={t}
                          onClick={() => {
                            setTime(t);
                            setStep(4);
                          }}
                          data-testid={`booking-slot-${t.replace(":", "")}`}
                          className={`border py-3.5 font-mono text-sm transition-all duration-300 hover:-translate-y-0.5 ${
                            time === t ? "border-brand-orange bg-brand-orange text-white" : "border-white/10 bg-navy/30 text-white hover:border-brand-orange hover:text-brand-orange"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                  <BackRow onBack={() => setStep(2)} />
                </motion.div>
              )}

              {step === 4 && (
                <motion.form key="s4" onSubmit={submit} initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }} transition={{ duration: 0.4 }} className="mt-10 space-y-5" data-testid="booking-form">
                  <p className="text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    {type.title} · {format(date, "dd/MM/yyyy")} às {time}
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Nome *</span>
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-input-name" className={inputCls} placeholder="Seu nome" />
                    </label>
                    <label className="block">
                      <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Empresa</span>
                      <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="booking-input-company" className={inputCls} placeholder="Nome da empresa" />
                    </label>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">E-mail *</span>
                      <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-input-email" className={inputCls} placeholder="voce@empresa.com.br" />
                    </label>
                    <label className="block">
                      <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">WhatsApp *</span>
                      <input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="booking-input-whatsapp" className={inputCls} placeholder="(47) 99999-0000" />
                    </label>
                  </div>
                  {type.id === "venda" && (
                    <div data-testid="booking-qualification" className="space-y-5 border border-brand-orange/30 bg-navy/40 p-6">
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-orange">
                        Qualificação — Reunião Estratégica
                      </p>
                      <p className="text-xs leading-relaxed text-slate-500">
                        Trabalhamos com um número limitado de projetos. Estas respostas ajudam a equipe a
                        confirmar as reuniões com maior aderência.
                      </p>
                      <label className="block">
                        <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Segmento da empresa *</span>
                        <input required value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} data-testid="booking-input-segment" className={inputCls} placeholder="Ex.: indústria, tecnologia, serviços..." />
                      </label>
                      {QUALIF.map((q) => (
                        <div key={q.key}>
                          <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">{q.label} *</span>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {q.options.map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setForm({ ...form, [q.key]: opt })}
                                data-testid={`booking-qualif-${q.key}-${q.options.indexOf(opt)}`}
                                className={`border px-4 py-3 text-left text-xs leading-snug transition-all duration-300 ${
                                  form[q.key] === opt
                                    ? "border-brand-orange bg-brand-orange text-white"
                                    : "border-white/15 text-slate-300 hover:border-brand-orange/60"
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={sending || (type.id === "venda" && (!form.segment || !form.revenue || !form.employees || !form.urgency || !form.investment))}
                    data-testid="booking-submit"
                    className="group flex w-full items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors hover:bg-brand-red disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />}
                    {sending ? "Confirmando..." : "Confirmar agendamento"}
                  </button>
                  <BackRow onBack={() => setStep(3)} />
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.main>
  );
}

const BackRow = ({ onBack }) => (
  <div className="mt-8 text-center">
    <button
      type="button"
      onClick={onBack}
      data-testid="booking-back-button"
      className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors hover:text-white"
    >
      <ArrowLeft size={13} /> Voltar
    </button>
  </div>
);
